import { motion } from "framer-motion";
import { MapPin, Dumbbell, Flame, Zap, Ruler, Weight } from "lucide-react";
import { DISCIPLINE_COLORS } from "@/lib/constants";
import { resolveImg } from "@/lib/api";

const StatChip = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1.5 text-xs text-zinc-300 bg-[#0d0d0d] border border-[#27272a] px-2.5 py-1.5 rounded-lg">
    <Icon size={13} className="text-zinc-500" />
    <span className="uppercase tracking-wide">{label}</span>
  </div>
);

export default function FighterCard({ fighter, onRequest }) {
  const camp = fighter.camp_mode;
  const dColor = DISCIPLINE_COLORS[fighter.discipline] || "#00FF66";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid={`fighter-card-${fighter.id}`}
      className={`group relative bg-card border rounded-2xl overflow-hidden transition-transform transition-colors duration-200 hover:-translate-y-1 ${
        camp
          ? "border-volt shadow-[0_0_18px_rgba(0,255,102,0.35)]"
          : "border-white/10 hover:border-volt/50"
      }`}
    >
      {camp && (
        <div
          data-testid={`camp-badge-${fighter.id}`}
          className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-volt text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full drop-shadow-[0_0_8px_rgba(0,255,102,0.8)]"
        >
          <Flame size={11} /> In Camp
        </div>
      )}

      <div className="relative h-48 overflow-hidden">
        {fighter.avatar_url ? (
          <img
            src={resolveImg(fighter.avatar_url)}
            alt={fighter.fighter_name}
            className="w-full h-full object-cover object-top grayscale-[30%] group-hover:grayscale-0 transition-all duration-300"
          />
        ) : (
          <div className="w-full h-full bg-[#0d0d0d] flex items-center justify-center">
            <span className="font-heading text-6xl text-zinc-800">
              {fighter.fighter_name?.[0]}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
        <span
          className="absolute bottom-3 left-4 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
          style={{ backgroundColor: dColor, color: "#000" }}
        >
          {fighter.discipline}
        </span>
      </div>

      <div className="p-4 pt-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-2xl uppercase tracking-tight leading-none">
            {fighter.fighter_name}
          </h3>
        </div>

        <div className="flex items-center gap-1.5 mt-2 text-sm text-zinc-400">
          <Dumbbell size={14} className="text-volt" />
          <span>{fighter.gym_name}</span>
        </div>

        <div className="flex items-center gap-1.5 mt-1">
          <MapPin size={13} className={fighter.same_city ? "text-volt" : "text-zinc-500"} />
          <span className={`text-xs uppercase tracking-wider ${fighter.same_city ? "text-volt" : "text-zinc-500"}`}>
            {fighter.city}{fighter.same_city ? " · Same City" : ""}
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          <StatChip icon={Weight} label={`${fighter.weight} ${fighter.weight_unit}`} />
          <StatChip icon={Ruler} label={fighter.height} />
          <StatChip icon={Zap} label={fighter.stance} />
          <StatChip icon={Flame} label={fighter.experience_level} />
        </div>

        <button
          data-testid={`request-rounds-${fighter.id}`}
          onClick={() => onRequest(fighter)}
          className="mt-4 w-full bg-volt text-black font-heading font-bold uppercase tracking-widest text-sm py-2.5 rounded-xl transition-colors duration-200 hover:bg-volt-hover flex items-center justify-center gap-2"
        >
          <Zap size={15} /> Request Rounds
        </button>
      </div>
    </motion.div>
  );
}
